import type { RunResult } from "@/api/adapt";
import type { NodeChangeRowDto } from "@/api/types";
import { fmtValue, pct } from "@/lib/format";
import { fmtMonth } from "@/lib/months";
import { headline, heroDate } from "@/lib/results";
import type { ModelNode, Lever, MetricNode, Scenario } from "@/model/types";

export { downstream, isCalculated } from "@/model/graph";

/** A scenario's chance, as the API reports it. */
export type ScenarioFigures = { probability: number | null; hypothetical: boolean; impossible: boolean };

/** Scenarios that set something on these nodes (e.g. a node and everything upstream of it). */
export function scenariosTouching(scenarios: Scenario[], ids: string[] | undefined): Scenario[] {
	if (!ids) return scenarios;
	const set = new Set(ids);
	return scenarios.filter((sc) => sc.levers.some((l) => set.has(l.node)));
}

/** A node's headline number: "28%" for an event, "$2.33M" for a metric. */
export function headlineText(n: ModelNode, r: RunResult): string {
	const v = headline(n, r, heroDate(n));
	return n.kind === "event" ? pct(v) : fmtValue(v, (n as MetricNode).unit);
}

/**
 * The biggest change a scenario makes that it doesn't set itself, e.g. "Layoffs needed 28% → 100%", from
 * the API's "What changes" (biggest first). Events come first (a metric swing is often just the lever's own
 * effect, like Acme revenue → $0); pass `preferEvents: false` to rank everything together. With `within`,
 * only those nodes count (e.g. everything downstream of the node being edited).
 */
export function biggestChange(
	nodes: Record<string, ModelNode>,
	changes: NodeChangeRowDto[],
	opts: { within?: Set<string>; preferEvents?: boolean } = {},
): { node: ModelNode; from: string; to: string } | null {
	const { within, preferEvents = true } = opts;
	const candidates = changes.filter((c) => c.moved && !c.set_here && nodes[c.handle] && (!within || within.has(c.handle)));
	const hit = (preferEvents && candidates.find((c) => c.kind === "event")) || candidates[0];
	if (!hit) return null;
	const f = (v: number) => (hit.kind === "event" ? pct(v) : fmtValue(v, hit.unit ?? ""));
	return { node: nodes[hit.handle], from: f(hit.baseline), to: f(hit.scenario) };
}

export function leverSentence(l: Lever, nodes: Record<string, ModelNode>): string {
	const n = nodes[l.node];
	if (!n) return l.node;
	if (l.kind === "event") {
		const what = { true: "happens", at: `happens in ${fmtMonth(l.month ?? "")}`, by: `happens by ${fmtMonth(l.month ?? "")}`, false: "doesn’t happen" }[
			l.happens
		];
		return `${n.title} ${what}`;
	}
	if (l.kind === "range") {
		const u = (n as MetricNode).unit;
		const last = Object.keys(l.range).sort().at(-1);
		return last ? `${n.title}: own range, likely ${fmtValue(l.range[last][1], u)} by ${fmtMonth(last)}` : `${n.title}: own range`;
	}
	const last = Object.keys(l.by).sort().at(-1);
	return last ? `${n.title}: own chance curve, ${pct(l.by[last])} by ${fmtMonth(last)}` : `${n.title}: own chance curve`;
}

/**
 * What the scenario's chance is the chance of, e.g. "Acme leaves in exactly Mar 2027 and
 * US recession by Dec 2027" (review E2). Only assumed outcomes count towards it.
 */
export function scenarioReason(scenario: Scenario, nodes: Record<string, ModelNode>): string {
	const parts = scenario.levers.flatMap((l) => {
		if (l.kind !== "event" || l.mode !== "assume") return [];
		const t = nodes[l.node]?.title ?? l.node;
		const m = fmtMonth(l.month ?? "2027-06");
		return [{ true: `${t} at some point`, at: `${t} in exactly ${m}`, by: `${t} by ${m}`, false: `no ${t}` }[l.happens]];
	});
	if (parts.length <= 1) return parts[0] ?? "";
	return `${parts.slice(0, -1).join(", ")} and ${parts.at(-1)}`;
}

/** The scenario's chance as a short label, or why it has none. */
export function scenarioChanceLabel(scenario: Scenario, r: ScenarioFigures): string {
	if (scenario.levers.length === 0) return "Same as Baseline";
	if (r.impossible) return "Impossible";
	if (r.hypothetical) return "Hypothetical";
	return pct(r.probability);
}
